import React, { useState } from 'react'
import styles from './ObjectsPopupSelector.module.scss'
import { SearchInput, Button, LabeledDropdown, PopupContainer, ObjectPreview, ToggleButton } from 'components'
import { getObjId } from 'utilities'
import { determineIfIsImage } from '../ObjectPreview'

const ObjectsPopupSelector = ({
  title = 'Select objects',
  onClose = null,
  objectsList = [],
  selectedObjectsList = [],
  setSelectedList = null,
  dropdownFilterOptions = [],
  dropdownLabel = 'Option',
  dropdownPlaceholder = 'Select',
  dropdownOnSelect = null,
  addOnly = false,
  submitButtonLabel = 'OK',
  onSubmit = null,
  toggleOptions = null,
  toggleSwitched = false,
  onToggleSwitch = () => null,
  topComponent = null
}) => {
  const [locallySelectedObjects, setLocallySelectedObjects] = useState({})
  const [dropDownSelection, setDropDownSelection] = useState(null)

  const toggleButtonOnClick = () => {
    setDropDownSelection(null)
    onToggleSwitch()
  }

  const handleDropdownOnSelect = value => {
    if (addOnly) {
      !locallySelectedObjects[value] && setLocallySelectedObjects(s => ({
        ...s,
        [value]: []
      }))
      addOnly && setDropDownSelection(value)
    }

    dropdownOnSelect(value)
  }

  const handleSelectObject = (id: number, selected: boolean) => {
    const cloneSelectedObjectsList = addOnly
      ? [...locallySelectedObjects[dropDownSelection]]
      : [...selectedObjectsList]

    if (selected) {
      const index = cloneSelectedObjectsList.findIndex(obj => getObjId(obj) === id)
      if (index < 0) return

      cloneSelectedObjectsList.splice(index, 1)
    } else {
      const index = objectsList.findIndex(obj => getObjId(obj) === id)
      if (index < 0) return

      cloneSelectedObjectsList.push({ ...objectsList[index] })
    }

    addOnly
      ? setLocallySelectedObjects({
        ...locallySelectedObjects,
        [dropDownSelection]: cloneSelectedObjectsList
      })
      : setSelectedList(cloneSelectedObjectsList)
  }

  const isObjectSelected = (id: number) => {
    return isObjectPresentInSelectedObjectList(id) || isObjectLocallySelected(id)
  }

  const isObjectLocallySelected = (id: number) => {
    if (!locallySelectedObjects[dropDownSelection]) return false
    const index = locallySelectedObjects[dropDownSelection].findIndex(obj => getObjId(obj) === id)
    return index > -1
  }

  const isObjectPresentInSelectedObjectList = (id: number) => {
    const index = selectedObjectsList.findIndex(obj => getObjId(obj) === id)
    return index > -1
  }

  const isObjectDisabled = (obj) => {
    if (obj.readOnly) return true
    if (!addOnly) return false
    return isObjectPresentInSelectedObjectList(getObjId(obj))
  }

  const handleSubmit = () => {
    onSubmit && onSubmit(locallySelectedObjects)
    onClose()
  }

  const areObjectsImages = determineIfIsImage(objectsList[0])

  const [searchFor, setSearchFor] = useState('')

  return (
    <PopupContainer
      onClose={onClose}
      title={title}
    >

      {topComponent}
      <div className={styles.filtersContainer}>
        {dropdownFilterOptions.length || toggleOptions
          ? <div>
            <LabeledDropdown
              key={dropdownFilterOptions.join()}
              options={dropdownFilterOptions}
              size='mini'
              label={dropdownLabel}
              placeholder={dropdownPlaceholder}
              onSelect={handleDropdownOnSelect}
            />

            {toggleOptions &&
              <div className={styles.toggleOptions}>
                <span style={!toggleSwitched ? { color: styles.blue } : {}}>
                  {toggleOptions[0]}
                </span>
                <ToggleButton
                  switchedOn={toggleSwitched}
                  onClick={toggleButtonOnClick}
                />
                <span style={toggleSwitched ? { color: styles.blue } : {}}>
                  {toggleOptions[1]}
                </span>
              </div>
            }

          </div>
          : <div />
        }

        {!areObjectsImages &&
          <SearchInput
            className={styles.searchBar}
            onChange={value => setSearchFor(value.trim().toLowerCase())}
          />
        }
      </div>

      <div className={styles.objectsListContainer}>
        <div className={styles.objectsListBody}>
          {objectsList
            .filter(obj => (obj.name || '').toLowerCase().includes(searchFor))
            .map(obj =>
              <ObjectPreview
                key={getObjId(obj)}
                object={obj}
                selected={isObjectSelected(getObjId(obj))}
                disabled={isObjectDisabled(obj)}
                handleSelect={handleSelectObject}
              />
            )}
        </div>
      </div>

      <Button
        type='submit'
        className={styles.okBttn}
        onClick={handleSubmit}
      >
        {submitButtonLabel}
      </Button>

    </PopupContainer>
  )
}

export default ObjectsPopupSelector
